import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { Module } from '@library/store/modules/modules.interface';
import { Room } from '@library/store/rooms/rooms.interface';
import { PlusMinusService } from '@library/ui/molecules/plus-minus/plus-minus.service';
import { Enums } from 'constants/Enums.constant';
import { Subscription, debounceTime, distinctUntilChanged, firstValueFrom, tap } from 'rxjs';
import { MainService } from 'src/app/modules/main/main.service';
import { SetPointService } from '../set-point.service';

@Component({
  selector: 'app-set-point',
  templateUrl: './set-point.component.html',
  styleUrls: ['./set-point.component.scss'],
})
export class SetPointComponent implements OnInit {

  @Input() room: Room;
  @Input() module: Module;

  EnumMeasureTypes = EnumMeasureTypes;

  minTemperature$ = this.homesFacade.hgTemperature$;

  step = 0.5;
  min: number;
  max = 30;

  form = new FormGroup({
    setPoint: new FormControl(null, Validators.required),
  });

  subscription: Subscription;

  constructor(
    private plusMinusService: PlusMinusService,
    private homesFacade: HomesFacade,
    private setPointService: SetPointService,
    private mainService: MainService,
  ) { }

  async ngOnInit(){
    this.min = await firstValueFrom(this.minTemperature$);
    // initialize form and service.
    this.form.patchValue({setPoint: this.room.therm_setpoint_temperature});
    this.setPointService.setPoint = this.room.therm_setpoint_temperature;

    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);

    // call api when + - not touched since 400ms
    this.subscription = this.form.controls.setPoint.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(async setPoint => {
        this.setPointService.setPoint = setPoint;
        if (setPoint === this.min && this.canModuleControlRoom() && this.room.therm_setpoint_mode !== 'off') {
          await firstValueFrom(this.homesFacade.setThermPoint(homeId, this.room.id, 'off'));
          this.mainService.loadHomeStatus();
        }
        else {
          if (this.room.therm_setpoint_mode !== 'off') {
            this.setPointService.changeSetPoint(homeId, this.room.id, 'manual');
          }
          else {
            await firstValueFrom(this.homesFacade.setThermPoint(homeId, this.room.id, 'home'));
            await this.mainService.loadHomeStatus();
            this.form.patchValue({setPoint: this.room.therm_setpoint_temperature});
          }
        }
      })
    ).subscribe();
  }

  add() {
    const val = this.plusMinusService.roundValue(this.form.get('setPoint').value + this.step, this.step, this.min, this.max, true, true);
    this.form.patchValue({
      setPoint: val
    });
  }

  subtract() {
    const val = this.plusMinusService.roundValue(this.form.get('setPoint').value - this.step, this.step, this.min, this.max, true, true);
    this.form.patchValue({
      setPoint: val
    });
  }

  isOff() {
    return this.room.therm_setpoint_mode === 'off' && this.canModuleControlRoom();
  }

  setPoint(setPoint: number) {
    return (setPoint && this.room.therm_setpoint_mode !== 'off') ? setPoint : '--';
  }

  colorClass(type: 'plus' | 'minus') {
    let colorClass = '';

    if ( this.isButtonDisabled(type) ) {
      colorClass += ' disabled ';
    }
    else {
      colorClass += ' is-light-grey ';
    }
    return colorClass;
  }

  canModuleControlRoom() {
    // For now only thermostat, one day we will have an array of devices supporting
    return this.module.type ===  Enums.ModuleType.NATHERM1;
  }

  isButtonDisabled(type: 'plus' | 'minus') {
    // buttons are diasbled if
    //  we are not off and we don't have any data (unreacahble)
    //  we are off and we can control the room -> only - button
    //  we are off and we cannot control the room -> both + and -
    //  we are have reached the minium temperature -> only min
    return (!this.room.therm_setpoint_temperature && this.room.therm_setpoint_mode !== 'off')
      || (this.room.therm_setpoint_mode === 'off' && this.canModuleControlRoom() && type === 'minus')
      || (this.room.therm_setpoint_mode === 'off' && !this.canModuleControlRoom())
      || (type === 'minus' && this.room.therm_setpoint_temperature === this.min);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
  isSetPointOn() {
    return this.room.therm_setpoint_mode === 'manual';
  }
}
